/**
 * ColorPicker Component
 *
 * Hex color input with preview swatch.
 */
import { useState, useEffect } from 'react';

interface ColorPickerProps {
  value: string;
  onChange: (color: string) => void;
  label?: string;
  error?: string;
}

const presetColors = [
  '#0D6433', // Metrica green
  '#1E40AF', // Blue
  '#7C3AED', // Purple
  '#DC2626', // Red
  '#EA580C', // Orange
  '#CA8A04', // Yellow
  '#16A34A', // Green
  '#0891B2', // Cyan
];

export const ColorPicker = ({ value, onChange, label, error }: ColorPickerProps) => {
  const [inputValue, setInputValue] = useState(value || '#0D6433');
  const [isValid, setIsValid] = useState(true);

  useEffect(() => {
    setInputValue(value || '#0D6433');
  }, [value]);

  const isValidHex = (color: string) => /^#[0-9A-Fa-f]{6}$/.test(color);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let newValue = e.target.value;

    // Auto-add # if not present
    if (newValue && !newValue.startsWith('#')) {
      newValue = '#' + newValue;
    }

    setInputValue(newValue);

    if (isValidHex(newValue)) {
      setIsValid(true);
      onChange(newValue.toUpperCase());
    } else {
      setIsValid(false);
    }
  };

  const handlePresetClick = (color: string) => {
    setInputValue(color);
    setIsValid(true);
    onChange(color);
  };

  return (
    <div className="mb-4">
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}

      <div className="flex items-center gap-3">
        {/* Color preview swatch */}
        <div
          className="w-10 h-10 rounded-lg border-2 border-gray-200 shadow-inner flex-shrink-0"
          style={{ backgroundColor: isValid ? inputValue : '#FFFFFF' }}
        />

        {/* Color input */}
        <div className="flex-1">
          <input
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            placeholder="#0D6433"
            maxLength={7}
            className={`
              w-full px-3 py-2 text-sm font-mono uppercase
              border rounded-lg
              focus:outline-none focus:ring-2 focus:ring-offset-0
              ${!isValid || error
                ? 'border-red-300 focus:ring-red-500'
                : 'border-gray-300 focus:ring-metrica-green'
              }
            `}
          />
        </div>

        {/* Native color picker */}
        <input
          type="color"
          value={isValid ? inputValue : '#0D6433'}
          onChange={(e) => handlePresetClick(e.target.value.toUpperCase())}
          className="w-10 h-10 rounded cursor-pointer border-0"
          title="Pick a color"
        />
      </div>

      {/* Preset colors */}
      <div className="flex gap-2 mt-2">
        {presetColors.map((color) => (
          <button
            key={color}
            type="button"
            onClick={() => handlePresetClick(color)}
            className={`
              w-6 h-6 rounded-full border-2 transition-transform hover:scale-110
              ${inputValue.toUpperCase() === color
                ? 'border-gray-800 ring-2 ring-offset-1 ring-gray-400'
                : 'border-gray-200'
              }
            `}
            style={{ backgroundColor: color }}
            title={color}
          />
        ))}
      </div>

      {(error || !isValid) && (
        <p className="mt-1 text-sm text-red-600">
          {error || 'Invalid hex color format (e.g., #0D6433)'}
        </p>
      )}
    </div>
  );
};

export default ColorPicker;
